import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMessageChannels, Channel, MentionProfile } from "@/hooks/useMessageChannels";
import { useThreadReplies } from "@/hooks/useThreadReplies";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, Send, Loader2, Hash, X, FileText, AtSign, Users, Search as SearchIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageAttachmentUpload, MessageAttachment, AttachmentPreviewBar, uploadFile, MAX_FILE_SIZE, ALLOWED_TYPES } from "@/components/messaging/MessageAttachmentUpload";
import { MessageInputDropzone } from "@/components/messaging/MessageInputDropzone";
import { MessageAttachmentDisplay } from "@/components/messaging/MessageAttachmentDisplay";
import { MessageReactions } from "@/components/messaging/MessageReactions";
import { MessageHoverActions } from "@/components/messaging/MessageHoverActions";
import { ThreadPreview } from "@/components/messaging/ThreadPreview";
import { ThreadPanel } from "@/components/messaging/ThreadPanel";
import { ManageChannelMembersDialog } from "@/components/messaging/ManageChannelMembersDialog";
import { MessageMarkdown } from "@/components/messaging/MessageMarkdown";
import { MessageFormatToolbar, handleFormatShortcuts } from "@/components/messaging/MessageFormatToolbar";

interface ChannelMessageThreadProps {
  channel: Channel;
  onBack: () => void;
}

interface LinkedLead {
  id: string;
  title: string;
}

interface Lead {
  id: string;
  title: string;
  client_name: string | null;
  status: string | null;
}

export const ChannelMessageThread = ({
  channel,
  onBack,
}: ChannelMessageThreadProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { messages, isLoadingMessages, sendChannelMessage, isSending, markChannelAsRead, profiles } = useMessageChannels(channel.id);
  const [newMessage, setNewMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [linkedLead, setLinkedLead] = useState<LinkedLead | null>(null);
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Thread replies hook
  const { replies: threadReplies, isLoading: isLoadingReplies, sendReply, isSending: isSendingReply } = useThreadReplies(activeThreadId || undefined, 'channel');
  
  // @mention state
  const [showMentionPopover, setShowMentionPopover] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [selectedMentions, setSelectedMentions] = useState<MentionProfile[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  // #lead state
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [leadSearch, setLeadSearch] = useState("");
  const [highlightedLeadIndex, setHighlightedLeadIndex] = useState(0);
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const LONG_MESSAGE_THRESHOLD = 300;
  const isLongMessage = (text: string) => text.length > LONG_MESSAGE_THRESHOLD;

  // Fetch leads for # dropdown
  const { data: leads = [] } = useQuery({
    queryKey: ["leads-for-channel-mention", leadSearch],
    queryFn: async () => {
      let query = supabase
        .from("leads")
        .select("id, title, client_name, status")
        .order("updated_at", { ascending: false });

      if (leadSearch) {
        // When searching, filter and limit for performance
        query = query.or(`title.ilike.%${leadSearch}%,client_name.ilike.%${leadSearch}%`)
          .limit(15);
      } else {
        // When not searching, show more leads
        query = query.limit(50);
      }

      const { data } = await query;
      return (data || []) as Lead[];
    },
    enabled: showLeadDropdown,
  });

  // Get current user ID
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data.session?.user?.id || null);
    });
  }, []);


  // Mark channel as read when opening
  useEffect(() => {
    if (channel.id) {
      markChannelAsRead(channel.id);
    }
  }, [channel.id, markChannelAsRead]);

  // Set up realtime subscription
  useEffect(() => {
    const subscription = supabase
      .channel(`channel-${channel.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'channel_messages',
          filter: `channel_id=eq.${channel.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["channel-messages", channel.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [channel.id, queryClient]);

  // Filter profiles for mention dropdown
  const filteredProfiles = profiles.filter(p => {
    if (!mentionSearch) return true;
    const name = p.full_name?.toLowerCase() || "";
    const email = p.email?.toLowerCase() || "";
    const search = mentionSearch.toLowerCase();
    return name.includes(search) || email.includes(search);
  }).slice(0, 8);

  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredProfiles.length, mentionSearch]);

  // Reset lead highlighted index when leads list changes
  useEffect(() => {
    setHighlightedLeadIndex(0);
  }, [leads.length, leadSearch]);

  // Insert a mention into the message
  const insertMention = (profile: MentionProfile) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const value = newMessage;
    const cursorPos = textarea.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const atIndex = textBefore.lastIndexOf("@");
    
    if (atIndex !== -1) {
      const displayName = profile.full_name || profile.email || "User";
      const newText = value.slice(0, atIndex) + `@${displayName} ` + value.slice(cursorPos);
      setNewMessage(newText);
      
      // Track this mention
      if (!selectedMentions.find(m => m.id === profile.id)) {
        setSelectedMentions([...selectedMentions, profile]);
      }
    }
    
    setShowMentionPopover(false);
    setMentionSearch("");
    setTimeout(() => textarea.focus(), 0);
  };

  // Insert a lead into the message
  const insertLead = (lead: Lead) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const value = newMessage;
    const cursorPos = textarea.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const hashIndex = textBefore.lastIndexOf("#");
    
    if (hashIndex !== -1) {
      const newText = value.slice(0, hashIndex) + `#${lead.title} ` + value.slice(cursorPos);
      setNewMessage(newText);
      setLinkedLead({ id: lead.id, title: lead.title });
    }
    
    setShowLeadDropdown(false);
    setLeadSearch("");
    setTimeout(() => textarea.focus(), 0);
  };

  // Handle input changes - detect @ for mentions and # for leads
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewMessage(value);

    const cursorPos = e.target.selectionStart;
    const textBefore = value.slice(0, cursorPos);

    // Check for # trigger (lead linking)
    const hashIndex = textBefore.lastIndexOf("#");
    if (hashIndex !== -1) {
      const charBeforeHash = hashIndex > 0 ? textBefore[hashIndex - 1] : " ";
      if (charBeforeHash === " " || charBeforeHash === "\n" || hashIndex === 0) {
        const searchText = textBefore.slice(hashIndex + 1);
        if (!searchText.includes(" ") && !searchText.includes("\n")) {
          setLeadSearch(searchText);
          setShowLeadDropdown(true);
          setShowMentionPopover(false);
          setMentionSearch("");
          return;
        }
      }
    }
    
    setShowLeadDropdown(false);
    setLeadSearch("");

    // Check for @ trigger
    const atIndex = textBefore.lastIndexOf("@");
    if (atIndex !== -1) {
      const charBeforeAt = atIndex > 0 ? textBefore[atIndex - 1] : " ";
      if (charBeforeAt === " " || charBeforeAt === "\n" || atIndex === 0) {
        const searchText = textBefore.slice(atIndex + 1);
        if (!searchText.includes(" ") && !searchText.includes("\n")) {
          setMentionSearch(searchText);
          setShowMentionPopover(true);
          return;
        }
      }
    }
    
    setShowMentionPopover(false);
    setMentionSearch("");
  };

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle lead dropdown navigation
    if (showLeadDropdown && leads.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedLeadIndex(prev => (prev + 1) % leads.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedLeadIndex(prev => (prev - 1 + leads.length) % leads.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertLead(leads[highlightedLeadIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowLeadDropdown(false);
        setLeadSearch("");
        return;
      }
    }

    // Handle mention popover navigation
    if (showMentionPopover && filteredProfiles.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex(prev => (prev + 1) % filteredProfiles.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(prev => (prev - 1 + filteredProfiles.length) % filteredProfiles.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredProfiles[highlightedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionPopover(false);
        setMentionSearch("");
        return;
      }
    }

    // Format shortcuts (Ctrl/Cmd + B/I/K)
    if (
      handleFormatShortcuts(
        e as React.KeyboardEvent<HTMLTextAreaElement>,
        textareaRef,
        newMessage,
        setNewMessage
      )
    ) {
      return;
    }

    // Send only with Ctrl/Cmd+Enter so Enter creates a newline (preserves formatting)
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle @ button click
  const handleAtButtonClick = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const cursorPos = textarea.selectionStart;
    const value = newMessage;
    const newText = value.slice(0, cursorPos) + "@" + value.slice(cursorPos);
    setNewMessage(newText);
    setShowMentionPopover(true);
    setMentionSearch("");
    setShowLeadDropdown(false);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos + 1, cursorPos + 1);
    }, 0);
  };

  // Handle # button click
  const handleHashButtonClick = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const cursorPos = textarea.selectionStart;
    const value = newMessage;
    const newText = value.slice(0, cursorPos) + "#" + value.slice(cursorPos);
    setNewMessage(newText);
    setShowLeadDropdown(true);
    setLeadSearch("");
    setShowMentionPopover(false);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos + 1, cursorPos + 1);
    }, 0);
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    
    try {
      // Include linked lead reference in message if present
      let messageText = newMessage.trim();
      if (linkedLead) {
        // Check if lead title is already in message (from inline #)
        if (!messageText.includes(`#${linkedLead.title}`)) {
          messageText = `[Lead: ${linkedLead.id}|${linkedLead.title}] ${messageText}`;
        } else {
          // Replace inline # with proper format including ID
          messageText = messageText.replace(`#${linkedLead.title}`, `[Lead: ${linkedLead.id}|${linkedLead.title}]`);
        }
      }
      
      // Extract mentioned user IDs
      const mentionedUserIds = selectedMentions
        .filter(m => newMessage.includes(`@${m.full_name || m.email}`))
        .map(m => m.id);
      
      await sendChannelMessage({ 
        channelId: channel.id, 
        message: messageText || "(attachment)",
        mentionedUserIds,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      setNewMessage("");
      setLinkedLead(null);
      setSelectedMentions([]);
      setAttachments([]);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "new": return "bg-blue-500/10 text-blue-500";
      case "quoted": return "bg-amber-500/10 text-amber-500";
      case "confirmed": return "bg-green-500/10 text-green-500";
      case "in_progress": return "bg-purple-500/10 text-purple-500";
      case "completed": return "bg-emerald-500/10 text-emerald-500";
      case "cancelled": return "bg-red-500/10 text-red-500";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getChannelColor = () => {
    if (channel.department?.color) {
      return channel.department.color;
    }
    return "hsl(var(--primary))";
  };

  // Parse message to detect and render lead references and @mentions
  const renderMessage = (message: string) => {
    // Handle @mentions in text
    const renderTextWithMentions = (text: string, keyPrefix: string) => {
      const mentionPattern = /@([^\s@]+(?:\s[^\s@]+)?)/g;
      const mentionParts = text.split(mentionPattern);
      
      return mentionParts.map((mPart, mIndex) => {
        const matchedProfile = profiles.find(p => 
          p.full_name === mPart || p.email === mPart
        );
        
        if (matchedProfile) {
          return (
            <span 
              key={`mention-${keyPrefix}-${mIndex}`}
              className="bg-primary/20 text-primary px-1 rounded"
            >
              @{mPart}
            </span>
          );
        }
        
        return <span key={`text-${keyPrefix}-${mIndex}`}>{mPart}</span>;
      });
    };

    // Pattern for new format [Lead: id|title] and legacy format [Lead: title]
    const newLeadPattern = /\[Lead: ([a-f0-9-]+)\|([^\]]+)\]/g;
    const legacyLeadPattern = /\[Lead: ([^\]|]+)\]/g;
    
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let processedMessage = message;
    
    // First, find all new format lead matches
    const newMatches = [...message.matchAll(newLeadPattern)];
    
    // Process new format leads
    newMatches.forEach((match, i) => {
      // Add text before this match
      if (match.index! > lastIndex) {
        const textBefore = message.slice(lastIndex, match.index);
        parts.push(...renderTextWithMentions(textBefore, `before-${i}`));
      }
      
      const [, leadId, leadTitle] = match;
      parts.push(
        <Tooltip key={`lead-${i}`}>
          <TooltipTrigger asChild>
            <Badge 
              variant="secondary" 
              className="mx-1 cursor-pointer hover:bg-primary/20 transition-colors"
              onClick={() => navigate(`/lead/${leadId}`)}
            >
              <FileText className="h-3 w-3 mr-1" />
              {leadTitle}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>Click to view lead</p>
          </TooltipContent>
        </Tooltip>
      );
      
      lastIndex = match.index! + match[0].length;
    });
    
    // If we found new format leads, add remaining text
    if (newMatches.length > 0) {
      if (lastIndex < message.length) {
        parts.push(...renderTextWithMentions(message.slice(lastIndex), 'after'));
      }
      return parts;
    }
    
    // Fallback: Handle legacy format [Lead: title] (without ID)
    const legacyMatches = [...message.matchAll(legacyLeadPattern)];
    lastIndex = 0;
    
    legacyMatches.forEach((match, i) => {
      if (match.index! > lastIndex) {
        const textBefore = message.slice(lastIndex, match.index);
        parts.push(...renderTextWithMentions(textBefore, `legacy-before-${i}`));
      }
      
      const [, leadTitle] = match;
      // Try to find the lead by title to get its ID
      const matchedLead = leads.find(l => l.title === leadTitle);
      
      if (matchedLead) {
        parts.push(
          <Tooltip key={`legacy-lead-${i}`}>
            <TooltipTrigger asChild>
              <Badge 
                variant="secondary" 
                className="mx-1 cursor-pointer hover:bg-primary/20 transition-colors"
                onClick={() => navigate(`/lead/${matchedLead.id}`)}
              >
                <FileText className="h-3 w-3 mr-1" />
                {leadTitle}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Click to view lead</p>
            </TooltipContent>
          </Tooltip>
        );
      } else {
        // No ID available, just render as non-clickable badge
        parts.push(
          <Badge 
            key={`legacy-lead-${i}`}
            variant="secondary" 
            className="mx-1"
          >
            <FileText className="h-3 w-3 mr-1" />
            {leadTitle}
          </Badge>
        );
      }
      
      lastIndex = match.index! + match[0].length;
    });
    
    if (legacyMatches.length > 0) {
      if (lastIndex < message.length) {
        parts.push(...renderTextWithMentions(message.slice(lastIndex), 'legacy-after'));
      }
      return parts;
    }
    
    // No leads found, just render mentions
    return renderTextWithMentions(message, 'nomatch');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div 
          className="h-8 w-8 rounded flex items-center justify-center"
          style={{ backgroundColor: getChannelColor() }}
        >
          <Hash className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1">
          <span className="font-semibold">{channel.name}</span>
          {channel.department && (
            <p className="text-xs text-muted-foreground">{channel.department.name} Department</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => { setShowSearch(!showSearch); setSearchQuery(""); }} title="Search messages">
            <SearchIcon className="h-4 w-4" />
          </Button>
          {channel.channel_type === "custom" && (
            <Button variant="ghost" size="icon" onClick={() => setShowManageMembers(true)} title="Manage members">
              <Users className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/50">
          <SearchIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setShowSearch(false); setSearchQuery(""); }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Manage Members Dialog */}
      <ManageChannelMembersDialog
        open={showManageMembers}
        onOpenChange={setShowManageMembers}
        channelId={channel.id}
        channelName={channel.name}
      />

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {isLoadingMessages ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Hash className="h-10 w-10 text-muted-foreground/50 mb-2" />
            <p className="text-muted-foreground text-sm">No messages in this channel yet</p>
            <p className="text-muted-foreground text-xs">Be the first to start a conversation!</p>
          </div>
        ) : (
          <div className="space-y-4 w-full min-w-0">
            {messages.filter(m => !m.parent_id).filter(m => !searchQuery || m.message.toLowerCase().includes(searchQuery.toLowerCase())).map((msg) => {
              const isOwn = msg.sender_id === currentUserId;
              const senderName = msg.sender?.full_name || msg.sender?.email || "?";
              return (
                <div
                  key={msg.id}
                  className="group relative flex gap-2"
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {getInitials(senderName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className={cn(
                        "text-sm font-medium",
                        isOwn && "text-primary"
                      )}>
                        {msg.sender?.full_name || msg.sender?.email || "Unknown"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div
                      className="mt-1 max-h-[200px] overflow-y-auto scrollable-content pr-1"
                      style={{ touchAction: 'pan-y' }}
                    >
                      <MessageMarkdown
                        message={msg.message}
                        profiles={profiles}
                        leads={leads as any}
                        onLeadClick={(id) => navigate(`/lead/${id}`)}
                      />
                    </div>
                    {msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                      <MessageAttachmentDisplay attachments={msg.attachments} />
                    )}
                    {/* Reactions */}
                    {currentUserId && (
                      <MessageReactions
                        messageId={msg.id}
                        messageType="channel"
                        currentUserId={currentUserId}
                      />
                    )}
                    {/* Thread Preview */}
                    {(msg.reply_count || 0) > 0 && (
                      <ThreadPreview
                        replyCount={msg.reply_count || 0}
                        onClick={() => setActiveThreadId(msg.id)}
                      />
                    )}
                  </div>
                  {/* Hover Actions */}
                  {currentUserId && (
                    <MessageHoverActions
                      onReply={() => setActiveThreadId(msg.id)}
                      onAddReaction={async (emoji) => {
                        const { data: session } = await supabase.auth.getSession();
                        if (!session?.session?.user) return;
                        await supabase.from("message_reactions").insert({
                          message_id: msg.id,
                          message_type: 'channel',
                          user_id: session.session.user.id,
                          emoji,
                        });
                        // Invalidate the reactions query to trigger refetch
                        queryClient.invalidateQueries({ queryKey: ["message-reactions", msg.id, "channel"] });
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Thread Panel */}
      {activeThreadId && messages.find(m => m.id === activeThreadId) && (
        <div className="w-80 border-l flex-shrink-0 absolute right-0 top-0 bottom-0 bg-background z-20">
          <ThreadPanel
            parentMessage={messages.find(m => m.id === activeThreadId)!}
            replies={threadReplies as any[]}
            currentUserId={currentUserId || ''}
            messageType="channel"
            onClose={() => setActiveThreadId(null)}
            onSendReply={async (message) => {
              await sendReply({ message, channelId: channel.id });
            }}
            isLoading={isLoadingReplies}
            isSending={isSendingReply}
          />
        </div>
      )}

      {/* Input */}
      <MessageInputDropzone
        onFilesDropped={async (files) => {
          const newAttachments: MessageAttachment[] = [];
          for (const file of files) {
            const attachment = await uploadFile(file);
            if (attachment) newAttachments.push(attachment);
          }
          if (newAttachments.length > 0) {
            setAttachments(prev => [...prev, ...newAttachments]);
          }
        }}
        disabled={isSending}
      >
      <div className="p-4 border-t">
        {/* Linked Lead Badge */}
        {linkedLead && (
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {linkedLead.title}
              <button 
                onClick={() => setLinkedLead(null)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          </div>
        )}
        
        <div className="relative">
          {/* #Lead Dropdown */}
          {showLeadDropdown && leads.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-72 bg-popover border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto">
              {leads.map((lead, index) => (
                <div
                  key={lead.id}
                  className={cn(
                    "flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-accent",
                    index === highlightedLeadIndex && "bg-accent"
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertLead(lead);
                  }}
                  onMouseEnter={() => setHighlightedLeadIndex(index)}
                >
                  <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{lead.title}</p>
                    {lead.client_name && (
                      <p className="text-xs text-muted-foreground truncate">{lead.client_name}</p>
                    )}
                  </div>
                  {lead.status && (
                    <Badge variant="secondary" className={cn("text-[10px] shrink-0", getStatusColor(lead.status))}>
                      {lead.status.replace(/_/g, " ")}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* @Mention Popover */}
          {showMentionPopover && filteredProfiles.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-64 bg-popover border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
              {filteredProfiles.map((profile, index) => (
                <div
                  key={profile.id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent",
                    index === highlightedIndex && "bg-accent"
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMention(profile);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {getInitials(profile.full_name || profile.email || "?")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {profile.full_name || "No name"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {profile.email}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <AttachmentPreviewBar
            attachments={attachments}
            onRemove={(id) => setAttachments(attachments.filter(a => a.id !== id))}
          />
          <MessageFormatToolbar
            textareaRef={textareaRef}
            value={newMessage}
            onChange={setNewMessage}
            disabled={isSending}
          />
          <Textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${channel.name}...`}
            className="min-h-[120px] pl-3 pr-3 pb-12 resize-y rounded-t-none border-t-0"
            disabled={isSending}
          />
          
          {/* @ and # Buttons */}
          <div className="absolute bottom-2 left-2 flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onMouseDown={(e) => {
                e.preventDefault();
                handleAtButtonClick();
              }}
              title="Mention a user (@)"
            >
              <AtSign className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onMouseDown={(e) => {
                e.preventDefault();
                handleHashButtonClick();
              }}
              title="Link a lead (#)"
            >
              <Hash className="h-4 w-4" />
            </Button>
            <MessageAttachmentUpload
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              disabled={isSending}
            />
          </div>

          <div className="absolute bottom-2 right-2">
            <Button
              size="icon"
              className="h-8 w-8"
              onClick={handleSend}
              disabled={(!newMessage.trim() && attachments.length === 0) || isSending}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Ctrl/Cmd+Enter to send · Enter for new line · @ mention · # lead · markdown supported
        </p>
      </div>
      </MessageInputDropzone>
    </div>
  );
};
